import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import * as Menu from "@/components/ui/menu";
import { Text } from "@/components/ui/text";
import { Textarea } from "@/components/ui/textarea";
import { Rank, appDialogConfirm } from "@components";
import { Avatar } from "@features/Avatar";
import { timeAgo } from "@features/communityContent/timeAgo";
import { useAuth, userId } from "@state/auth";
import { Edit, EllipsisVertical, Reply, Trash2, X } from "lucide-solid";
import { For, Show, createSignal } from "solid-js";
import { Portal } from "solid-js/web";
import { useService } from 'solid-services';
import { Box, Flex, Grid } from "styled-system/jsx";
import { Content } from "../Content";
import { Post } from "../Post";
import { useComments } from '../state';
import type { CommentTreeItem } from '../types';

interface BubbleItemProps {
  comment: CommentTreeItem;
  isReply?: boolean;
}

export const BubbleItem = (props: BubbleItemProps) => {
  const { activeReplyId, openReply, closeReply, editMutation, deleteMutation } = useService(useComments)();
  const { isAdmin } = useService(useAuth)();

  const [isEditing, setIsEditing] = createSignal(false);
  const [editContent, setEditContent] = createSignal(props.comment.content);

  const isReplying = () => activeReplyId() === props.comment.id;
  const canModify = () => isAdmin() || props.comment.profile_id === userId()!;

  const handleEdit = async () => {
    if (!editContent().trim() || editContent() === props.comment.content) {
      setIsEditing(false);
      return;
    }
    await editMutation.mutateAsync({ id: props.comment.id, content: editContent() });
    setIsEditing(false);
  };

  const handleDelete = async () => {
    appDialogConfirm(
      () => deleteMutation.mutateAsync(props.comment.id),
      'Are you sure you want to delete this comment?',
    )();
  };

  return (
    <Grid gap="12px" ml={props.isReply ? '44px' : '0'}>
      <Flex gap="11px" alignItems="flex-start">
        <Box position="relative" flexShrink={0} w="34px" h="34px">
          <Avatar id={props.comment.profile.id} />
          <Show when={props.comment.profile.profile_private?.role}>
            <Box
              position="absolute"
              right="-3px"
              bottom="-3px"
              borderRadius="full"
              bg="bg.emphasized"
              boxShadow="0 0 0 1px var(--colors-editorial-hair-strong)"
            >
              <Rank rank={props.comment.profile.profile_private?.role} dimensions="14px" />
            </Box>
          </Show>
        </Box>

        <Grid gap="4px" minWidth={0} flex="1">
          <Flex alignItems="baseline" gap="7px">
            <Text fontWeight="700" fontSize="13px" color="editorialInk" textTransform="capitalize">
              {props.comment.profile.username}
            </Text>
            <Text fontSize="11px" color="editorialInk3">
              {timeAgo(props.comment.created_at)}
            </Text>
          </Flex>

          <Show
            when={!isEditing()}
            fallback={
              <Box>
                <Textarea
                  value={editContent()}
                  onInput={(e) => setEditContent(e.currentTarget.value)}
                  autofocus
                />
                <Flex justify="flex-end" gap="2" mt="2">
                  <Button variant="outline" size="sm" onClick={() => setIsEditing(false)} disabled={editMutation.isPending}>
                    Cancel
                  </Button>
                  <Button size="sm" onClick={handleEdit} loading={editMutation.isPending} disabled={!editContent().trim()}>
                    Save
                  </Button>
                </Flex>
              </Box>
            }
          >
            <Box
              bg="editorialBubble"
              borderRadius="4px 13px 13px 13px"
              px="14px"
              py="11px"
              w="fit-content"
              maxW="full"
              color="editorialInk"
            >
              <Content content={props.comment.content} noPadding />
            </Box>
          </Show>

          <Flex gap="2" alignItems="center">
            <Show
              when={isReplying()}
              fallback={
                <Button variant="plain" size="2xs" color="editorialInk3" px="0" h="auto" onClick={() => openReply(props.comment.id)}>
                  <Reply size={12} />
                  Reply
                </Button>
              }
            >
              <Button variant="plain" size="2xs" color="error.fg" px="0" h="auto" onClick={closeReply}>
                <X size={12} />
                Cancel
              </Button>
            </Show>

            <Show when={canModify() && !isEditing()}>
              <Menu.Root
                onSelect={(details) => {
                  if (details.value === 'edit') setIsEditing(true);
                  if (details.value === 'delete') handleDelete();
                }}
              >
                <Menu.Trigger
                  asChild={(triggerProps) => (
                    <IconButton variant="plain" size="2xs" color="editorialInk3" {...triggerProps()}>
                      <EllipsisVertical size={13} />
                    </IconButton>
                  )}
                />
                <Portal>
                  <Menu.Positioner>
                    <Menu.Content>
                      <Menu.Item minH={8} value="edit">
                        <Edit size={14} />
                        <Text textStyle="xs">Edit</Text>
                      </Menu.Item>
                      <Menu.Item minH={8} value="delete" color="error.fg">
                        <Trash2 size={14} />
                        <Text textStyle="xs">Delete</Text>
                      </Menu.Item>
                    </Menu.Content>
                  </Menu.Positioner>
                </Portal>
              </Menu.Root>
            </Show>
          </Flex>

          <Show when={isReplying()}>
            <Post onCancel={closeReply} parentId={props.comment.id} />
          </Show>
        </Grid>
      </Flex>

      <For each={props.comment.replies}>
        {(reply) => <BubbleItem comment={reply} isReply />}
      </For>
    </Grid>
  );
};
